/**
 * Implements the deep Completion log module. Callers record and observe
 * Completions; durable storage, remote merging, and synchronization stay here.
 */
import type { Completion } from '@life-rpg/shared';

export type CompletionLogStatus = 'loading' | 'ready' | 'unavailable';

export interface CompletionLogSnapshot {
  readonly completions: readonly Completion[];
  readonly pendingCount: number;
  readonly problem: string | null;
  readonly status: CompletionLogStatus;
}

export interface CompletionLog {
  getSnapshot: () => CompletionLogSnapshot;
  record: (activityId: string, qty: number) => Promise<void>;
  subscribe: (listener: () => void) => () => void;
}

export interface CompletionRemote {
  list: () => Promise<Completion[]>;
  save: (completion: Completion) => Promise<void>;
}

export interface CompletionOutbox {
  put: (completion: Completion) => Promise<void>;
  list: () => Promise<Completion[]>;
  remove: (id: string) => Promise<void>;
}

export interface ConnectivityEvents {
  addEventListener: (type: 'online', listener: EventListener) => void;
  removeEventListener: (type: 'online', listener: EventListener) => void;
}

interface CompletionLogOptions {
  createId?: () => string;
  now?: () => Date;
}

const mergeCompletions = (
  saved: readonly Completion[],
  pending: readonly Completion[],
): Completion[] => {
  const byId = new Map(pending.map((completion) => [completion.id, completion]));
  for (const completion of saved) byId.set(completion.id, completion);
  return [...byId.values()].sort((left, right) => left.at.localeCompare(right.at));
};

const problemMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Could not load completions.';

export const createCompletionLog = (
  remote: CompletionRemote,
  outbox: CompletionOutbox,
  connectivity: ConnectivityEvents,
  { createId = () => crypto.randomUUID(), now = () => new Date() }: CompletionLogOptions = {},
): CompletionLog => {
  let snapshot: CompletionLogSnapshot = {
    completions: [],
    pendingCount: 0,
    problem: null,
    status: 'loading',
  };
  let activeFlush: Promise<void> | null = null;
  let refreshSequence = 0;
  const listeners = new Set<() => void>();

  const publish = (next: CompletionLogSnapshot): void => {
    snapshot = next;
    for (const listener of listeners) listener();
  };

  const refresh = async (): Promise<void> => {
    const sequence = ++refreshSequence;
    const pending = await outbox.list();

    try {
      const saved = await remote.list();
      if (sequence !== refreshSequence) return;
      publish({
        completions: mergeCompletions(saved, pending),
        pendingCount: pending.length,
        problem: null,
        status: 'ready',
      });
    } catch (error) {
      if (sequence !== refreshSequence) return;
      publish({
        completions: mergeCompletions(snapshot.completions, pending),
        pendingCount: pending.length,
        problem: problemMessage(error),
        status: 'unavailable',
      });
    }
  };

  const flush = async (): Promise<void> => {
    if (activeFlush !== null) {
      const previousFlush = activeFlush;
      try {
        await previousFlush;
      } catch {
        // A later synchronization request is still an opportunity to retry.
      }
      if (activeFlush === previousFlush) activeFlush = null;
      return flush();
    }

    const work = (async (): Promise<void> => {
      for (const completion of await outbox.list()) {
        await remote.save(completion);
        await outbox.remove(completion.id);
      }
    })();
    activeFlush = work;

    try {
      await work;
    } finally {
      if (activeFlush === work) activeFlush = null;
    }
  };

  const synchronize = async (): Promise<void> => {
    try {
      await flush();
    } catch {
      // A durable local write remains successful while the remote is unavailable.
    }
    await refresh();
  };

  const reportUnexpectedProblem = (error: unknown): void => {
    publish({ ...snapshot, problem: problemMessage(error), status: 'unavailable' });
  };

  const synchronizeInBackground = (): void => {
    void synchronize().catch(reportUnexpectedProblem);
  };

  const handleOnline: EventListener = () => {
    synchronizeInBackground();
  };

  return {
    getSnapshot: () => snapshot,
    record: async (activityId, qty) => {
      const completion: Completion = {
        id: createId(),
        activityId,
        at: now().toISOString(),
        qty,
        energy: null,
        dayAtWrite: null,
      };
      await outbox.put(completion);
      const pending = await outbox.list();
      publish({
        ...snapshot,
        completions: mergeCompletions(snapshot.completions, pending),
        pendingCount: pending.length,
      });
      synchronizeInBackground();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) {
        connectivity.addEventListener('online', handleOnline);
        synchronizeInBackground();
      }

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) connectivity.removeEventListener('online', handleOnline);
      };
    },
  };
};
